import { Component, ElementRef, viewChild, input, effect, HostListener } from '@angular/core';
import { axisBottom, axisLeft, NumberValue, scaleBand, scaleLinear, select, stack } from 'd3';
import { formatValue } from '../../../helpers/kpiWidget/kpi-formatters.helper';
import { truncateSvgText } from '../../../helpers/graphs/svg-text.helper';

export interface StackedBarChartData {
  [key: string]: string | number;
}

export interface StackedBarSubgroup {
  name: string;
  legend: string;
  color: string;
}

export interface LegendItem {
  legend: string;
  color: string;
}

@Component({
  selector: 'app-stacked-bar-chart',
  standalone: true,
  imports: [],
  templateUrl: './stacked-bar-chart.component.html',
  styleUrl: './stacked-bar-chart.component.scss'
})
export class StackedBarChartComponent {
  readonly chartContainer = viewChild<ElementRef>('stackedBarChart');

  public data = input.required<StackedBarChartData[]>();
  public margin = input({ top: 31, right: 10, bottom: 20, left: 0 });
  public yTicks = input<number[]>([100, 80, 60, 40, 20, 0]);
  public chartHeight = input.required<number>();
  public labelKey = input<string>('label');
  public subgroups = input<StackedBarSubgroup[]>([]);
  public legend = input<LegendItem[]>([]);
  public stackOrder = input<string[] | null>(null);
  public tooltipUnit = input<string>('R');
  public unitSuffix = input<string>('');
  public barWidthPercentage = input<number>(0.6);
  public barWidthPx = input<number | null>(null);
  // Columns carry disjoint key sets (e.g. one key per asset/geofence pair):
  // the tooltip lists only the hovered column's keys, labelled by the
  // subgroup legend instead of the raw key.
  public uniqueColumnKeys = input<boolean>(false);

  constructor() {
    effect(() => {
      if (this.data()?.length && this.chartContainer()?.nativeElement && this.margin() && this.yTicks() && this.chartHeight()) {
        this.createChart(
          this.data(),
          this.chartContainer()?.nativeElement,
          this.margin(),
          this.yTicks(),
          this.chartHeight(),
          this.labelKey()
        );
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    if (this.data()?.length && this.chartContainer()?.nativeElement && this.margin() && this.yTicks() && this.chartHeight()) {
      this.createChart(
        this.data(),
        this.chartContainer()?.nativeElement,
        this.margin(),
        this.yTicks(),
        this.chartHeight(),
        this.labelKey()
      );
    }
  }

  createChart(
    data: StackedBarChartData[],
    element: HTMLElement,
    margin: any,
    yTicks: number[],
    fullHeight: number,
    labelKey: string
  ) {
    const fullWidth = element.getBoundingClientRect().width;
    const width = fullWidth - margin.left - margin.right;
    const height = fullHeight - margin.top - margin.bottom;

    select(element).selectAll('svg').remove();
    select(element).selectAll('.tooltip').remove();

    const svg = select(element)
      .append('svg')
      .attr('width', fullWidth)
      .attr('height', fullHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // const subgroupConfig = this.subgroups();
    // const subgroupNames = subgroupConfig.map(s => s.name);

    const subgroupConfig = this.subgroups();
    const defaultNames = subgroupConfig.map(s => s.name);
    const subgroupByName = new Map(subgroupConfig.map(s => [s.name, s]));
    const colorMap = new Map(subgroupConfig.map(s => [s.name, s.color]));

    let orderedKeys: string[] = [];
    const customOrder = this.stackOrder();

    if (customOrder?.length) {
      orderedKeys = customOrder.filter(name => subgroupByName.has(name));
    } else {
      orderedKeys = [...defaultNames];
    }

    for (const name of defaultNames) {
      if (!orderedKeys.includes(name)) orderedKeys.push(name);
    }

    const groups = data.map(d => d[labelKey] as string);
    const y = scaleLinear().domain([0, Math.max(...yTicks)]).range([height, 0]);

    // Create temporary y-axis to measure label width
    const tmpAxis = axisLeft(y)
      .tickValues(yTicks)
      .tickFormat(d => this.formatValue(d))
      .tickSize(0);

    const tmp = svg.append('g').attr('class', 'yAxisTmp').call(tmpAxis);
    const maxLabelWidth = Math.ceil(
      Math.max(0, ...tmp.selectAll('text').nodes().map((n: any) => n.getBBox().width))
    );
    tmp.remove();

    const leftPad = maxLabelWidth + 10;
    const innerWidth = Math.max(0, width - leftPad);
    const plot = svg.append('g').attr('transform', `translate(${leftPad},0)`);

    // Y-axis
    const yAxis = axisLeft(y)
      .tickValues(yTicks)
      .tickFormat(d => this.formatValue(d))
      .tickSize(-innerWidth)
      .tickPadding(10);

    const yAxisGroup = plot.append('g').call(yAxis);
    yAxisGroup.select('.domain').remove();
    yAxisGroup.selectAll('line').attr('stroke', '#E0E0E0');
    yAxisGroup.selectAll('text')
      .attr('text-anchor', 'end')
      .style('font-family', '"Nunito Sans", sans-serif')
      .style('fill', '#9FA1A4')
      .style('font-size', '10px');

    // X-axis
    const fixedBarWidth = this.barWidthPx();
    let x: any;
    let barWidth: number;

    if (fixedBarWidth !== null) {
      // Use fixed pixel width
      barWidth = fixedBarWidth;
      const totalWidth = groups.length * barWidth;
      const spacing = (innerWidth - totalWidth) / (groups.length - 1 || 1);
      x = scaleBand()
        .domain(groups)
        .range([0, innerWidth])
        .paddingInner(spacing / (spacing + barWidth))
        .paddingOuter(0);
    } else {
      // Use percentage width
      x = scaleBand()
        .domain(groups)
        .range([0, innerWidth])
        .padding(1 - this.barWidthPercentage());
      barWidth = x.bandwidth();
    }

    plot.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(axisBottom(x))
      .call(g => {
        g.selectAll('line').remove();
        g.select('.domain').remove();

        // Position tick marks based on bar positions
        if (fixedBarWidth !== null && groups.length > 1) {
          const totalBarsWidth = groups.length * barWidth;
          const totalGapWidth = innerWidth - totalBarsWidth;
          const gapBetweenBars = totalGapWidth / (groups.length - 1);

          g.selectAll('g').each((d, i, nodes) => {
            const tick = select(nodes[i]);
            if (i !== 0 && i !== nodes.length - 1) {
              // Calculate position: sum of previous bars + sum of previous gaps + half current bar
              const position = (i * barWidth) + (i * gapBetweenBars) + (barWidth / 2);
              tick.attr('transform', `translate(${position},0)`);
            }
          });
        }

        // Distance between adjacent tick centers, for both layout modes
        const tickSpacing = fixedBarWidth !== null && groups.length > 1
          ? (innerWidth - barWidth) / (groups.length - 1)
          : x.step();
        const labelGap = 8;
        g.selectAll('text')
          .style('fill', '#9FA1A4')
          .style('font-family', '"Nunito Sans", sans-serif')
          .style('font-size', '10px')
          .each((d, i, nodes) => {
            const text = select(nodes[i]);
            if (i === 0) {
              // First tick: align left
              text.attr('text-anchor', 'start')
                .attr('x', fixedBarWidth !== null ? -barWidth / 2 : -x.bandwidth() / 2);
            } else if (i === nodes.length - 1) {
              // Last tick: align right
              text.attr('text-anchor', 'end')
                .attr('x', fixedBarWidth !== null ? barWidth / 2 : x.bandwidth() / 2);
            } else {
              // Middle ticks: center
              text.attr('text-anchor', 'middle')
                .attr('x', 0);
            }

            // Edge labels start at the bar's outer edge, so they get half a
            // slot plus half a bar width before reaching the neighbour's slot
            const maxWidth = nodes.length === 1
              ? innerWidth
              : i === 0 || i === nodes.length - 1
                ? (tickSpacing + barWidth) / 2 - labelGap
                : tickSpacing - labelGap;
            truncateSvgText(nodes[i] as SVGTextElement, Math.max(14, maxWidth));
          });
      });

    // Create stack layout; columns may only carry their own subset of keys
    const stackGenerator = stack<StackedBarChartData>()
      .keys(orderedKeys)
      .value((d, key) => Number(d[key]) || 0);
    const series = stackGenerator(data);
    const tooltipKeys = series.reverse().map(s => s.key);

    // Tooltip
    const tooltip = select(element)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', '#fff')
      .style('border-radius', '8px')
      .style('padding', '4px 6px')
      .style('box-shadow', '0 4px 10px rgba(0, 0, 0, 0.08)')
      .style('border', '1px solid #e0e0e0')
      .style('pointer-events', 'none')
      .style('font-family', '"Nunito Sans", sans-serif')
      .style('z-index', '999');

    const component = this;
    const uniqueColumnKeys = this.uniqueColumnKeys();

    // Draw stacked bars
    plot.append('g')
      .selectAll('g')
      .data(series)
      .enter()
      .append('g')
      .attr('fill', d => colorMap.get(d.key)!)
      .selectAll('rect')
      .data(d => d)
      .enter()
      .append('rect')
      .attr('x', d => x((d.data as any)[labelKey])!)
      .attr('y', d => y(d[1]))
      .attr('height', d => y(d[0]) - y(d[1]))
      .attr('width', barWidth)
      .on('mouseover', function(event, d) {
        const current = d.data;
        tooltip
          .style('opacity', 1)
          .html(`
            <div style="font-weight: 600; font-size: 13px; color: #2B2B2B; margin-bottom: 1px;">
              ${current[labelKey]}
            </div>
            ${tooltipKeys.filter(name => !uniqueColumnKeys || current[name] !== undefined).map(name => `
              <div style="display: flex; align-items: center; margin-bottom: 1px; font-family: 'Nunito Sans', sans-serif;">
                <div style="width: 12px; height: 12px; background: ${colorMap.get(name)}; border-radius: 50%; margin-right: 6px;"></div>
                <div style="color: #9FA1A4; font-size: 12px; flex: 1; margin-right: 8px;">${subgroupByName.get(name)?.legend ?? name}</div>
                <div style="color: #9FA1A4; font-size: 12px;">${component.formatTooltipValue(current[name] as number)}</div>
              </div>`).join('')}
          `);
      })
      .on('mousemove', function(event) {
        const tooltipEl = tooltip.node() as HTMLDivElement;
        const tooltipWidth = tooltipEl.offsetWidth;
        const containerRect = element.getBoundingClientRect();

        if (containerRect.right - event.clientX < tooltipWidth) {
          tooltip
            .style('left', event.offsetX - tooltipWidth - 5 + 'px')
            .style('top', event.offsetY - 28 + 'px');
        } else {
          tooltip
            .style('left', event.offsetX + 10 + 'px')
            .style('top', event.offsetY - 28 + 'px');
        }
      })
      .on('mouseout', function() {
        tooltip.style('opacity', 0);
      });
  }

  private formatValue(data: NumberValue): string {
    const currencySymbol = this.tooltipUnit();
    return formatValue(data as number, { currencySymbol }).trimEnd() + this.unitSuffix();
  }

  private formatTooltipValue(value: number): string {
    const currencySymbol = this.tooltipUnit();
    const formattedValue = value.toLocaleString();

    if (currencySymbol) {
      // Currency format
      return `${currencySymbol}${formattedValue}`;
    }
    return `${formattedValue}${this.unitSuffix()}`;
  }
}